import type { ReactNode } from 'react';

import Image from 'next/image';
import Link from 'next/link';

import {
  ArrowDown,
  ArrowRight,
  BookOpen,
  Check,
  CircleHelp,
  Layers3,
  MessageCircle,
  Send,
  Users,
} from 'lucide-react';

import { Button } from '@kit/ui/button';
import { AppLogo } from '@kit/ui/patterns/app-logo';
import { Trans } from '@kit/ui/trans';
import { cn } from '@kit/ui/utils';

import { WebsiteGlassSurface } from './website-glass-surface';
import pearlStyles from './website-pearl-button.module.css';
import {
  WebsiteWorkflowDemos,
  type WebsiteWorkflowDemosProps,
} from './website-workflow-demos';

export function WebsiteDisplayHeading({
  children,
  id,
  as: Tag = 'h1',
}: {
  children: ReactNode;
  id?: string;
  as?: 'h1' | 'p';
}) {
  return (
    <Tag
      id={id}
      data-website-heading
      className="max-w-3xl text-5xl leading-[1.02] font-medium tracking-[-0.055em] sm:text-6xl xl:text-7xl"
    >
      {children}
    </Tag>
  );
}

export function WebsiteLead({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <p
      className={cn(
        'text-muted-foreground max-w-xl text-lg leading-relaxed',
        className,
      )}
    >
      {children}
    </p>
  );
}

export function WebsiteBrandLockup({
  className,
  compact = false,
}: {
  className?: string;
  compact?: boolean;
}) {
  return (
    <div
      data-website-brand
      className={cn('flex flex-col items-start gap-3', className)}
    >
      <div role="img" aria-label="Leagen — Always on, anytime, everywhere">
        <AppLogo
          href={null}
          slogan
          className={compact ? 'w-56' : 'w-72 sm:w-80'}
        />
      </div>
    </div>
  );
}

type WebsiteEyebrowProps = (
  | { children: ReactNode; variant?: 'dot'; badge?: never }
  | { children: ReactNode; variant: 'pill'; badge: ReactNode }
) & { tone?: 'neutral' | 'accent' };

export function WebsiteEyebrow(props: WebsiteEyebrowProps) {
  if (props.variant === 'pill') {
    return (
      <WebsiteGlassSurface
        as="p"
        material="pill"
        className={cn(
          'inline-flex max-w-full flex-wrap items-center gap-x-2 gap-y-1 p-1 pr-3 text-sm font-medium',
          props.tone === 'accent' && 'ring-1 ring-[#5b3df5]/10',
        )}
      >
        <span
          className={cn(
            'shrink-0 rounded-full bg-[#171717] px-3 py-1.5 text-xs leading-5 font-semibold text-white',
            props.tone === 'accent' && 'bg-[#5b3df5]',
          )}
        >
          {props.badge}
        </span>
        <span className="min-w-0 px-1 py-1 leading-snug">{props.children}</span>
      </WebsiteGlassSurface>
    );
  }

  return (
    <p className="text-muted-foreground flex items-center gap-2 text-xs font-medium tracking-[0.14em] uppercase">
      <span
        className={cn(
          'bg-foreground h-1.5 w-1.5 shrink-0 rounded-full',
          props.tone === 'accent' && 'bg-[#5b3df5]',
        )}
        aria-hidden
      />
      {props.children}
    </p>
  );
}

export function WebsiteSectionHeading({
  eyebrow,
  title,
  description,
  id,
  className,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  id: string;
  className?: string;
}) {
  return (
    <div className={cn('max-w-2xl space-y-5', className)}>
      {eyebrow && <WebsiteEyebrow>{eyebrow}</WebsiteEyebrow>}
      <h2
        data-website-section-heading
        id={id}
        className="text-3xl leading-[1.08] font-medium tracking-[-0.045em] sm:text-4xl lg:text-5xl"
      >
        {title}
      </h2>
      {description && (
        <p className="text-muted-foreground max-w-xl text-base leading-relaxed sm:text-lg">
          {description}
        </p>
      )}
    </div>
  );
}

export function WebsiteLinkButton({
  href,
  children,
  variant = 'default',
  tone = 'neutral',
  arrow = true,
  className,
}: {
  href: string;
  children: ReactNode;
  variant?: 'default' | 'outline' | 'ghost';
  tone?: 'neutral' | 'accent';
  arrow?: boolean | 'down';
  className?: string;
}) {
  const accentStyles = {
    default: pearlStyles.pearl,
    outline:
      'border-[#5b3df5]/40 text-[#5b3df5] hover:bg-[#5b3df5]/10 hover:text-[#4930cf] dark:text-[#b7a8ff] dark:hover:text-[#d0c6ff]',
    ghost:
      'text-[#5b3df5] hover:bg-[#5b3df5]/10 hover:text-[#4930cf] dark:text-[#b7a8ff] dark:hover:text-[#d0c6ff]',
  };

  return (
    <Button
      asChild
      variant={variant}
      className={cn(
        'h-auto min-h-12 max-w-full gap-3 rounded-full px-6 py-3 text-sm whitespace-normal shadow-none focus-visible:ring-2 focus-visible:ring-offset-4',
        tone === 'accent' && accentStyles[variant],
        className,
      )}
    >
      <Link href={href} data-website-button={variant} data-button-tone={tone}>
        <span>{children}</span>
        {arrow &&
          (arrow === 'down' ? (
            <ArrowDown className="h-4 w-4 shrink-0" aria-hidden />
          ) : (
            <ArrowRight className="h-4 w-4 shrink-0" aria-hidden />
          ))}
      </Link>
    </Button>
  );
}

export function WebsiteFeatureCard({
  icon,
  title,
  description,
  number,
}: {
  icon: ReactNode;
  title: ReactNode;
  description: ReactNode;
  number?: string;
}) {
  return (
    <WebsiteGlassSurface
      as="article"
      className="flex h-full min-w-0 flex-col gap-6 rounded-2xl p-6 sm:p-7"
    >
      <div className="flex items-center justify-between gap-4">
        <span
          className="bg-muted flex h-11 w-11 items-center justify-center rounded-xl"
          aria-hidden
        >
          {icon}
        </span>
        {number && (
          <span className="text-muted-foreground font-mono text-xs" aria-hidden>
            {number}
          </span>
        )}
      </div>
      <div className="space-y-3">
        <h3 className="text-lg font-medium tracking-tight">{title}</h3>
        <p className="text-muted-foreground text-sm leading-relaxed">
          {description}
        </p>
      </div>
    </WebsiteGlassSurface>
  );
}

export function WebsiteWorkflow(props: WebsiteWorkflowDemosProps) {
  return <WebsiteWorkflowDemos {...props} />;
}

/** Conceptual platform overview: deliberately not a fabricated product dashboard. */
export function WebsitePlatformComposition() {
  return (
    <WebsiteGlassSurface
      as="figure"
      className="relative isolate min-w-0 overflow-hidden rounded-[1.75rem] p-5 sm:p-8"
    >
      <div
        className="pointer-events-none absolute inset-0 -z-10 [background-image:radial-gradient(var(--muted-foreground)_0.6px,transparent_0.6px)] [background-size:20px_20px] opacity-30"
        aria-hidden
      />
      <figcaption className="text-muted-foreground mb-8 flex items-center justify-between gap-3 text-xs">
        <span>
          <Trans i18nKey="marketing:home.platformOverview" />
        </span>
        <Layers3 className="h-4 w-4" aria-hidden />
      </figcaption>
      <div className="mx-auto flex max-w-sm flex-col items-center">
        <div className="border-border bg-background flex w-full items-center gap-3 rounded-2xl border p-4 shadow-sm">
          <BookOpen
            className="text-muted-foreground h-5 w-5 shrink-0"
            aria-hidden
          />
          <div className="space-y-1">
            <p className="text-sm font-medium">
              <Trans i18nKey="marketing:home.flow.knowledge.title" />
            </p>
            <p className="text-muted-foreground text-xs">
              <Trans i18nKey="marketing:home.sourceExamples" />
            </p>
          </div>
        </div>
        <ArrowDown className="text-muted-foreground my-3 h-5 w-5" aria-hidden />
        <div className="border-border bg-background w-full rounded-2xl border px-6 py-7 shadow-sm">
          <WebsiteBrandLockup compact className="items-center text-center" />
          <div className="border-border text-muted-foreground mt-5 border-t pt-4 text-center text-xs">
            <Trans i18nKey="marketing:home.agentsAtWork" />
          </div>
        </div>
        <ArrowDown className="text-muted-foreground my-3 h-5 w-5" aria-hidden />
        <div className="grid w-full grid-cols-2 gap-3">
          <div className="border-border bg-background flex min-w-0 flex-col items-center gap-3 rounded-2xl border p-4 text-center text-xs shadow-sm">
            <MessageCircle className="h-5 w-5" aria-hidden />
            <Trans i18nKey="marketing:home.customerConversations" />
          </div>
          <div className="border-border bg-background flex min-w-0 flex-col items-center gap-3 rounded-2xl border p-4 text-center text-xs shadow-sm">
            <Users className="h-5 w-5" aria-hidden />
            <Trans i18nKey="marketing:home.teamInControl" />
          </div>
        </div>
      </div>
    </WebsiteGlassSurface>
  );
}

/** Static synthetic conversation with Aiman's existing, locally cached avatar. */
export function WebsiteConversationPreview({
  variant = 'answer',
}: {
  variant?: 'answer' | 'question';
}) {
  return (
    <WebsiteGlassSurface
      as="figure"
      className="mx-auto w-full max-w-[360px] min-w-0 overflow-hidden rounded-3xl"
    >
      <figcaption className="border-border flex items-center gap-3 border-b px-4 py-3.5">
        <Image
          src="/images/agents/aiman-avatar.jpg"
          alt="Aiman"
          width={512}
          height={512}
          className="h-10 w-10 shrink-0 rounded-full"
        />
        <span className="min-w-0 space-y-0.5">
          <span className="block text-sm font-medium">Aiman</span>
          <span className="text-muted-foreground block text-xs">
            <Trans i18nKey="marketing:home.aimanPreview.role" />
          </span>
        </span>
      </figcaption>
      <div className="flex min-h-52 flex-col gap-3 p-4">
        <div className="bg-primary text-primary-foreground ml-7 self-end rounded-2xl rounded-br-sm px-3.5 py-2.5 text-sm leading-relaxed">
          <Trans i18nKey="marketing:home.aimanPreview.question" />
        </div>
        {variant === 'answer' ? (
          <div className="mr-5 space-y-2.5 self-start">
            <p className="bg-muted rounded-2xl rounded-bl-sm px-3.5 py-2.5 text-sm leading-relaxed">
              <Trans i18nKey="marketing:home.aimanPreview.answer" />
            </p>
            <p className="text-muted-foreground flex items-center gap-1.5 px-1 text-[11px]">
              <Check className="h-3 w-3 shrink-0" aria-hidden />
              <Trans i18nKey="marketing:home.aimanPreview.nextStep" />
            </p>
          </div>
        ) : (
          <p className="text-muted-foreground flex items-center gap-2 text-sm">
            <CircleHelp className="h-4 w-4" aria-hidden />
            <Trans i18nKey="marketing:home.questionState" />
          </p>
        )}
      </div>
      <div className="px-3 pb-3" aria-hidden="true">
        <div className="border-border text-muted-foreground flex items-center justify-between rounded-full border py-1.5 pr-1.5 pl-3.5 text-xs">
          <Trans i18nKey="marketing:home.workflowDemo.messagePlaceholder" />
          <span className="bg-primary text-primary-foreground flex h-8 w-8 items-center justify-center rounded-full">
            <Send className="h-3.5 w-3.5" />
          </span>
        </div>
      </div>
      <p className="border-border text-muted-foreground border-t px-4 py-2.5 text-[11px] leading-relaxed">
        <Trans i18nKey="marketing:home.aimanPreview.disclaimer" />
      </p>
    </WebsiteGlassSurface>
  );
}

export function WebsiteTeamFeatures() {
  const features = [
    {
      title: 'Bring your team together',
      description:
        'Invite members into your workspace and manage who belongs to your team.',
      icon: Users,
    },
    {
      title: 'Give each person the right access',
      description:
        'Manage member roles and permissions so people can work with the access they need.',
      icon: Users,
    },
    {
      title: 'Guide the agent privately',
      description:
        'Send an immediate instruction or leave context for the agent while it continues helping your customer.',
      icon: BookOpen,
    },
    {
      title: 'Reach the right escalation group',
      description:
        'Group the people responsible for enrolments, support or specialist questions. Notify that team when a conversation needs attention.',
      icon: Users,
    },
    {
      title: 'Take over. Hand back.',
      description:
        'A team member can take over the conversation with its history in view, then hand it back when the agent can continue.',
      icon: MessageCircle,
    },
    {
      title: 'Turn gaps into better answers',
      description:
        'Review escalation resolutions, update the agent’s knowledge and train it for future conversations.',
      icon: BookOpen,
    },
  ];
  return (
    <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
      {features.map(({ title, description, icon: Icon }) => (
        <WebsiteFeatureCard
          key={title}
          icon={<Icon className="h-5 w-5" />}
          title={title}
          description={description}
        />
      ))}
    </div>
  );
}
